import { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Spinner, Tabs, Tab } from 'react-bootstrap';
import { usePagedArray, Pager } from 'rentalui-finance';
import { getMySharingInvite, getTodaysDeliveries, markDelivered, markPickedUp, respondToSharingInvite } from '../api';
import ProductDetailsModal from '../components/ProductDetailsModal';
import OrderDetailsModal from '../components/OrderDetailsModal';
import DeliveryList from '../components/DeliveryList';
import DeliveryTicketPanel from '../components/DeliveryTicketPanel';
import DeclineDeliveryModal from '../components/DeclineDeliveryModal';
import MyAvailabilityPanel from '../components/MyAvailabilityPanel';
import DeliveryPayablesPanel from '../components/DeliveryPayablesPanel';
import FailedAttemptModal from '../components/FailedAttemptModal';
import CollectCashModal from '../components/CollectCashModal';
import { scheduleAttention } from '../components/scheduleAttention';
import InfoTip from '../components/InfoTip';
import { deliveryStatusReason } from '../components/statusHelp';
import { useAuth } from '../auth';

// Restricted Delivery/Operations view (MyBookings user story 9.2) - no pricing, commission, or
// payout fields anywhere here; the API itself never sends them to this role (see
// BookingRepository.toRoleView's 'delivery' branch). v3_fix_issues #13 - the "My schedule" tab
// now shows ONLY what's assigned to this delivery person (outbound pickups/drop-offs AND return
// collections), and a Cash-on-Delivery booking gets a "Collect cash" action. Story 17 adds the
// ability to decline an assignment and a "My availability" tab.
export default function DeliveryDashboard() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [productItemId, setProductItemId] = useState(null);
  const [orderDetailsBooking, setOrderDetailsBooking] = useState(null); // fix_issues3.md - order popup
  const [declineBooking, setDeclineBooking] = useState(null); // Story 17
  const [collectCashBooking, setCollectCashBooking] = useState(null); // v3_fix_issues #13
  const [failedAttemptBooking, setFailedAttemptBooking] = useState(null); // shared_delivery_inter_business_billing.md item 14
  const [sharingInvite, setSharingInvite] = useState(null); // common/v3/delivery_agent_pool_scoping.md
  const [sharingBusy, setSharingBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setBookings(await getTodaysDeliveries());
    } catch {
      setError('Could not load today\'s deliveries.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSharingInvite = useCallback(() => {
    getMySharingInvite().then(setSharingInvite).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    loadSharingInvite();
  }, [load, loadSharingInvite]);

  const respondSharing = async (accept) => {
    setSharingBusy(true);
    try {
      await respondToSharingInvite(accept);
      loadSharingInvite();
    } finally {
      setSharingBusy(false);
    }
  };

  const handlePickup = async (id) => {
    setBusyId(id);
    try {
      await markPickedUp(id);
      load();
    } finally {
      setBusyId(null);
    }
  };

  const handleDelivered = async (id) => {
    setBusyId(id);
    try {
      await markDelivered(id);
      load();
    } finally {
      setBusyId(null);
    }
  };

  const schedulePaged = usePagedArray(bookings);

  if (loading) return <Spinner animation="border" role="status" />;
  if (error) return <Alert variant="danger">{error}</Alert>;

  return (
    <div>
      <h3 className="mb-3">Deliveries</h3>

      {sharingInvite?.sharingInvite?.status === 'pending' && (
        <Alert variant="info" className="d-flex flex-wrap align-items-center gap-2">
          <span className="flex-grow-1">
            The platform is asking if you're willing to take deliveries for <strong>other businesses</strong> on it,
            not only the owner who onboarded you.
          </span>
          <Button size="sm" variant="success" disabled={sharingBusy} onClick={() => respondSharing(true)}>Yes, I'm in</Button>
          <Button size="sm" variant="outline-secondary" disabled={sharingBusy} onClick={() => respondSharing(false)}>Not now</Button>
        </Alert>
      )}
      {sharingInvite?.availableForOtherOwners && sharingInvite?.sharingInvite?.status === 'accepted' && (
        <Alert variant="light" className="py-2 small">
          You're in the shared delivery pool — businesses across car rental &amp; jewellery can assign you.
        </Alert>
      )}

      <Tabs defaultActiveKey="today" className="mb-3">
        <Tab eventKey="today" title="My schedule">
          {schedulePaged.total === 0 ? (
            <Alert variant="light">Nothing assigned to you to deliver, pick up or collect right now.</Alert>
          ) : (
            <div className="row g-3">
              {schedulePaged.rows.map((booking) => (
                <div className="col-md-6 col-lg-4" key={booking._id}>
                  <Card className={scheduleAttention(booking) ? 'rv-schedule-attention' : ''}>
                    <Card.Body>
                      {scheduleAttention(booking) && (
                        <Badge bg="dark" className="rv-schedule-attention-badge mb-2">
                          <span aria-hidden="true">⚠</span> Reminder: {scheduleAttention(booking)}
                        </Badge>
                      )}
                      <Button
                        variant="link" size="sm"
                        className="p-0 align-baseline text-muted small font-monospace"
                        onClick={() => setOrderDetailsBooking(booking)}
                      >
                        #{booking._id.slice(-6).toUpperCase()}
                      </Button>
                      <div className="fw-semibold mb-2">{booking.customerName}</div>
                      <div className="small mb-1">📞 {booking.phoneNumber}</div>
                      {booking.deliveryTicket ? (
                        <DeliveryTicketPanel ticket={booking.deliveryTicket} className="mb-2" />
                      ) : booking.deliveryAddress?.line1 && (
                        <div className="small mb-1">
                          📍 {[booking.deliveryAddress.line1, booking.deliveryAddress.line2, booking.deliveryAddress.area, booking.deliveryAddress.city, booking.deliveryAddress.pincode].filter(Boolean).join(', ')}
                        </div>
                      )}
                      <div className="small mb-1">
                        Date: {new Date(booking.startDate).toLocaleDateString()}
                      </div>
                      <div className="small text-muted">Status: <InfoTip text={deliveryStatusReason(booking)}><span>{booking.deliveryStatus.replace(/_/g, ' ')}</span></InfoTip></div>
                      {['RETURN_SCHEDULED', 'PICKED_UP', 'RECEIVED', 'INSPECTION_PENDING'].includes(booking.returnStatus) && (
                        <div className="small text-muted">Return: {booking.returnStatus.replace(/_/g, ' ')}</div>
                      )}
                      {booking.cashOnDelivery && (
                        <div className="small mb-1">
                          {booking.codCollected
                            ? <span className="text-success">Cash collected (₹{(booking.codAmountDue || 0).toLocaleString('en-IN')})</span>
                            : <span className="fw-semibold">Cash on delivery: ₹{(booking.codAmountDue || 0).toLocaleString('en-IN')}</span>}
                        </div>
                      )}
                      <div className="d-flex flex-wrap gap-2 mt-2">
                        <Button size="sm" variant="outline-secondary" onClick={() => setProductItemId(booking.itemId)}>
                          View product
                        </Button>
                        <Button
                          size="sm" variant="outline-dark"
                          href={`tel:${booking.phoneNumber}`}
                        >
                          Call Customer
                        </Button>
                        {booking.deliveryStatus === 'PENDING' && (
                          <Button size="sm" variant="outline-primary" disabled={busyId === booking._id} onClick={() => handlePickup(booking._id)}>
                            Mark Picked Up
                          </Button>
                        )}
                        {['PICKED_UP', 'IN_TRANSIT'].includes(booking.deliveryStatus) && (
                          <Button size="sm" variant="success" disabled={busyId === booking._id} onClick={() => handleDelivered(booking._id)}>
                            Mark Delivered
                          </Button>
                        )}
                        {['PENDING', 'PICKED_UP', 'IN_TRANSIT'].includes(booking.deliveryStatus) && (
                          <Button size="sm" variant="outline-warning" onClick={() => setFailedAttemptBooking(booking)}>
                            Customer unavailable
                          </Button>
                        )}
                        {booking.cashOnDelivery && !booking.codCollected && (
                          <Button size="sm" variant="outline-success" onClick={() => setCollectCashBooking(booking)}>
                            Collect cash
                          </Button>
                        )}
                        {/* Story 17 - only shown when this booking is actually assigned to me
                            (this queue is platform-wide, not scoped to "assigned to me" - see
                            routes/delivery.js's /today comment). */}
                        {String(booking.deliveryAssignment?.assignedTo) === String(user?.id) && (
                          <Button size="sm" variant="outline-danger" onClick={() => setDeclineBooking(booking)}>
                            Can't make it
                          </Button>
                        )}
                        {booking.deliveryNeedsReassignment && (
                          <Badge bg="warning" text="dark" className="align-self-center">Needs reassignment</Badge>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </div>
              ))}
            </div>
          )}
          <Pager {...schedulePaged} />
        </Tab>

        {/* Story 14 - what's actually assigned to me, upcoming vs past, not the whole ops queue. */}
        <Tab eventKey="mine" title="My Deliveries">
          <DeliveryList />
        </Tab>

        {/* Story 17 - self-managed unavailable date/time ranges. */}
        <Tab eventKey="availability" title="My availability">
          <MyAvailabilityPanel />
        </Tab>

        {/* delivery_person_payables.md - what business owners owe me per completed leg. */}
        <Tab eventKey="earnings" title="Payments" mountOnEnter>
          <DeliveryPayablesPanel />
        </Tab>
      </Tabs>

      <ProductDetailsModal itemId={productItemId} show={!!productItemId} onHide={() => setProductItemId(null)} />
      <OrderDetailsModal
        booking={orderDetailsBooking}
        show={!!orderDetailsBooking}
        onHide={() => setOrderDetailsBooking(null)}
        onViewProduct={orderDetailsBooking ? () => setProductItemId(orderDetailsBooking.itemId) : undefined}
      />
      <DeclineDeliveryModal
        booking={declineBooking}
        show={!!declineBooking}
        onHide={() => setDeclineBooking(null)}
        onDeclined={() => {
          setDeclineBooking(null);
          load();
        }}
      />
      <CollectCashModal
        booking={collectCashBooking}
        show={!!collectCashBooking}
        onHide={() => setCollectCashBooking(null)}
        onCollected={load}
      />
      <FailedAttemptModal
        booking={failedAttemptBooking}
        show={!!failedAttemptBooking}
        onHide={() => setFailedAttemptBooking(null)}
        onRecorded={() => { setFailedAttemptBooking(null); load(); }}
      />
    </div>
  );
}
